import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { RootContext } from "../../context/RootContext";
import { login } from "../../services/auth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  User,
  Lock,
  Eye,
  EyeOff,
  LogIn,
  Phone,
  Shield,
  Zap,
  Star,
  Heart,
  Sparkles,
  Circle,
  Triangle,
  Square,
  X,
  ArrowLeft,
  ArrowRight,
  Check,
  KeyRound,
} from "lucide-react";
import axios from '../../services/axios';

// Floating geometric shapes
const FloatingShape = ({ children, delay = 0, duration = 6, initialX = 0, initialY = 0 }) => (
  <motion.div
    className="absolute pointer-events-none"
    initial={{
      x: initialX,
      y: initialY,
      opacity: 0.3,
      rotate: 0,
      scale: 0.5,
    }}
    animate={{
      x: [initialX, initialX + 100, initialX - 50, initialX],
      y: [initialY, initialY - 80, initialY + 60, initialY],
      opacity: [0.3, 0.6, 0.2, 0.3],
      rotate: [0, 180, 360],
      scale: [0.5, 0.8, 0.6, 0.5],
    }}
    transition={{
      repeat: Infinity,
      duration,
      delay,
      ease: "easeInOut",
    }}
  >
    {children}
  </motion.div>
);

// Rotating orbiting elements
const OrbitingIcon = ({ icon: Icon, radius, duration, delay, size = 24 }) => (
  <motion.div
    className="absolute top-1/2 left-1/2"
    animate={{ rotate: 360 }}
    transition={{
      repeat: Infinity,
      duration,
      delay,
      ease: "linear",
    }}
  >
    <div
      className="relative text-blue-300/40"
      style={{ transform: `translateX(${radius}px) translateY(-50%)` }}
    >
      <motion.div
        animate={{ rotate: -360 }}
        transition={{
          repeat: Infinity,
          duration,
          delay,
          ease: "linear",
        }}
      >
        <Icon size={size} />
      </motion.div>
    </div>
  </motion.div>
);

// Gradient blob background
const GradientBlob = ({ className, delay = 0 }) => (
  <motion.div
    className={`absolute rounded-full blur-3xl ${className}`}
    initial={{ scale: 0.8, opacity: 0.1 }}
    animate={{
      scale: [0.8, 1.3, 0.9, 1.1, 0.8],
      opacity: [0.1, 0.2, 0.15, 0.25, 0.1],
      x: [0, 30, -20, 10, 0],
      y: [0, -20, 25, -10, 0],
    }}
    transition={{
      repeat: Infinity,
      duration: 12,
      delay,
      ease: "easeInOut",
    }}
  />
);

// Sparkle animation
const Sparkle = ({ delay, x, y }) => (
  <motion.div
    className="absolute w-2 h-2 bg-gradient-to-r from-yellow-200 to-pink-200 rounded-full"
    style={{ left: x, top: y }}
    initial={{ scale: 0, opacity: 0 }}
    animate={{
      scale: [0, 1.5, 0],
      opacity: [0, 1, 0],
      rotate: [0, 180, 360],
    }}
    transition={{
      repeat: Infinity,
      duration: 2,
      delay,
      ease: "easeInOut",
    }}
  />
);

// Forgot Password Modal Component
const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    phone: "",
    username: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      phone: value,
    }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    const phoneRegex = /^\+?[\d\s-]{10,}$/;

    if (!formData.phone.trim()) {
      newErrors.phone = "Please enter your phone number";
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!formData.username.trim()) {
      newErrors.username = "Please enter your username";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!formData.otp.trim()) {
      newErrors.otp = "Please enter the OTP";
    } else if (formData.otp.length !== 6) {
      newErrors.otp = "OTP must be 6 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = "Please enter a new password";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const verifyUser = async () => {
    try {
      setLoading(true);
      const res = await axios.post('/get-user-exist', { user: { username: formData.username, phone: formData.phone } });

      if (res.data?.users) {
        // Check if the provided username and phone number match any user
        const userMatch = res.data.users.some(
          (user) => user.username === formData.username && user.phone === formData.phone
        );
        if (userMatch) {
          return true; // User found with matching username and phone
        } else {
          setErrors((prev) => ({
            ...prev,
            username: "Username or phone number does not match any account",
          }));
          return false;
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          username: "No users found. Please try again.",
        }));
        return false;
      }
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.status === 404) {
        message.error("Invalid username or phone number");
      } else if (err.response && err.response.status === 401) {
        message.error("User not authorized");
      } else {
        message.error("Something went wrong");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
  setLoading(true);

  try {
    if (currentStep === 1 && validateStep1()) {
      // Verify user with API
      const isValidUser = await verifyUser();
      if (isValidUser) {
        // Call backend to send OTP
        const otpResponse = await axios.post('/send-otp', {
          username: formData.username,
          phone: formData.phone,
        });

        if (otpResponse.data?.success) {
          toast.success("OTP sent to your phone", { position: "top-center", autoClose: 2000 });
          setCurrentStep(2);
        } else {
          toast.error("Failed to send OTP. Try again.", { position: "top-center", autoClose: 2000 });
        }
      }
    } else if (currentStep === 2 && validateStep2()) {
      // Verify OTP (you can implement backend verification here)
      const verifyResponse = await axios.post('/verify-otp', {
        username: formData.username,
        phone: formData.phone,
        otp: formData.otp,
      });

      if (verifyResponse.data?.success) {
        toast.success("OTP verified successfully", { position: "top-center", autoClose: 2000 });
        setCurrentStep(3);
      } else {
        toast.error("Invalid OTP", { position: "top-center", autoClose: 2000 });
      }
    } else if (currentStep === 3 && validateStep3()) {
      // Reset password
      const resetResponse = await axios.post('/reset-password', {
        username: formData.username,
        newPassword: formData.newPassword,
      });

      if (resetResponse.status === 200 && resetResponse.data.message === "User reset password successfully") {
        toast.success("Password reset successfully", { position: "top-center", autoClose: 2000 });
        handleClose();
      } else {
        toast.error("Failed to reset password", { position: "top-center", autoClose: 2000 });
      }
    }
  } catch (error) {
    toast.error("Something went wrong. Please try again.", { position: "top-center", autoClose: 2000 });
  }

  setLoading(false);
};

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({ phone: "", username: "", otp: "", newPassword: "", confirmPassword: "" });
    setErrors({});
    setLoading(false);
    onClose();
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2, ease: "easeIn" },
    },
  };

  const stepContent = {
    1: {
      title: "Reset Password",
      subtitle: "Enter your username and phone number to receive OTP",
      icon: <Phone className="w-8 h-8 text-blue-500" />,
      content: (
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">Username</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="Enter your username"
                className={`w-full pl-10 pr-3 py-3 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.username ? "border-red-300 focus:ring-red-500" : "border-gray-200 hover:border-blue-400"
                }`}
              />
            </div>
            {errors.username && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm mt-1"
              >
                {errors.username}
              </motion.p>
            )}
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Phone Number</label>
            <PhoneInput
              country="om"
              value={formData.phone}
              onChange={handlePhoneChange}
              inputProps={{
                name: "phone",
                required: true,
                className: `w-full p-1.5 pl-12 border rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.phone ? "border-red-300 focus:ring-red-500" : "border-gray-200 hover:border-blue-400"
                }`,
              }}
            />
            {errors.phone && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm mt-1"
              >
                {errors.phone}
              </motion.p>
            )}
          </div>
        </div>
      ),
    },
    2: {
      title: "Verify OTP",
      subtitle: "Enter the 6-digit code sent to your phone",
      icon: <Shield className="w-8 h-8 text-green-500" />,
      content: (
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">OTP Code</label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                name="otp"
                value={formData.otp}
                onChange={handleInputChange}
                placeholder="Enter 6-digit OTP"
                maxLength="6"
                className={`w-full pl-10 pr-3 py-3 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-center tracking-widest font-mono text-lg ${
                  errors.otp ? "border-red-300 focus:ring-red-500" : "border-gray-200 hover:border-green-400"
                }`}
              />
            </div>
            {errors.otp && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm mt-1"
              >
                {errors.otp}
              </motion.p>
            )}
          </div>
          {/* <p className="text-sm text-gray-500 text-center"> */}
            {/* Didn't receive the code?{" "} */}
            {/* <button className="text-blue-500 hover:text-blue-600 font-medium">Resend OTP</button> */}
          {/* </p> */}
        </div>
      ),
    },
    3: {
      title: "New Password",
      subtitle: "Create a strong new password",
      icon: <Lock className="w-8 h-8 text-purple-500" />,
      content: (
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type={showPassword ? "text" : "password"}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
                className={`w-full pl-10 pr-10 py-3 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  errors.newPassword ? "border-red-300 focus:ring-red-500" : "border-gray-200 hover:border-purple-400"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.newPassword && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm mt-1"
              >
                {errors.newPassword}
              </motion.p>
            )}
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm new password"
                className={`w-full pl-10 pr-10 py-3 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  errors.confirmPassword ? "border-red-300 focus:ring-red-500" : "border-gray-200 hover:border-purple-400"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm mt-1"
              >
                {errors.confirmPassword}
              </motion.p>
            )}
          </div>
        </div>
      ),
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>

            {/* Step indicator */}
            <div className="flex justify-center mb-6">
              <div className="flex space-x-2">
                {[1, 2, 3].map((step) => (
                  <div
                    key={step}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                      step === currentStep
                        ? "bg-blue-500 text-white"
                        : step < currentStep
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {step < currentStep ? <Check size={16} /> : step}
                  </div>
                ))}
              </div>
            </div>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-3">{stepContent[currentStep].icon}</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{stepContent[currentStep].title}</h2>
              <p className="text-gray-600">{stepContent[currentStep].subtitle}</p>
            </div>

            {/* Content */}
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-6"
            >
              {stepContent[currentStep].content}
            </motion.div>

            {/* Actions */}
            <div className="flex justify-between space-x-3">
              {currentStep > 1 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <ArrowLeft size={18} />
                  <span>Back</span>
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNext}
                disabled={loading}
                className={`${
                  currentStep === 1 ? "w-full" : "flex-1"
                } py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 ${
                  loading ? "opacity-75 cursor-not-allowed" : ""
                }`}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin h-4 w-4 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  <>
                    <span>{currentStep === 3 ? "Reset Password" : "Next"}</span>
                    {currentStep < 3 && <ArrowRight size={18} />}
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ username: "", password: "", remember: true });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [sparkles, setSparkles] = useState([]);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const { dispatch } = useContext(RootContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const citriot = JSON.parse(localStorage.getItem("citriot"));
    if (citriot?.auth?.isLoggedIn) {
      navigate("/landingPage");
    }

    // Generate random sparkles
    const newSparkles = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      delay: Math.random() * 3,
    }));
    setSparkles(newSparkles);
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = "Please enter your username";
    }
    if (!formData.password) {
      newErrors.password = "Please enter your password";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const { data, error } = await login(formData.username, formData.password);
      if (error) {
        setLoading(false);
        if (error.response?.data) {
          return toast.error(error.response.data, { position: "top-center", autoClose: 2000 });
        } else if (error.response?.status === 401) {
          return toast.error("User not authorized", { position: "top-center", autoClose: 2000 });
        }
        return toast.error("Something went wrong", { position: "top-center", autoClose: 2000 });
      }
      message.success('Login Successful');
      dispatch({ type: "LOGIN", user: data.user });
      const isSuperUser = localStorage.getItem("isSuperUser") === "true";
      navigate(isSuperUser ? "/home" : "/landingPage");
    } catch (err) {
      setLoading(false);
      if (err.response?.data?.msg) {
        toast.warning(err.response.data.msg, { position: "top-center", autoClose: 2000 });
      } else if (err.response?.data?.userName) {
        toast.warning(err.response.data.userName[0], { position: "top-center", autoClose: 2000 });
      } else if (err.response?.status === 401) {
        toast.error("User not authorized", { position: "top-center", autoClose: 2000 });
      } else {
        toast.error("Something went wrong", { position: "top-center", autoClose: 2000 });
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1,
        ease: "easeOut",
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
      <ToastContainer position="top-center" autoClose={2000} hideProgressBar closeOnClick />
      
      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPasswordModal} 
        onClose={() => setShowForgotPasswordModal(false)} 
      />
      
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient blobs */}
        <GradientBlob
          className="w-96 h-96 bg-gradient-to-r from-blue-200/30 to-indigo-200/30 -top-20 -left-20"
          delay={0}
        />
        <GradientBlob
          className="w-80 h-80 bg-gradient-to-r from-purple-200/25 to-pink-200/25 -bottom-10 -right-10"
          delay={2}
        />
        <GradientBlob
          className="w-64 h-64 bg-gradient-to-r from-cyan-200/20 to-blue-200/20 top-1/3 left-1/4"
          delay={4}
        />
        <GradientBlob
          className="w-72 h-72 bg-gradient-to-r from-indigo-200/25 to-purple-200/25 top-10 right-1/4"
          delay={6}
        />

        {/* Floating geometric shapes */}
        <FloatingShape delay={0} duration={8} initialX={100} initialY={150}>
          <Circle className="w-8 h-8 text-blue-300/40" />
        </FloatingShape>
        <FloatingShape delay={1} duration={6} initialX={300} initialY={200}>
          <Triangle className="w-6 h-6 text-indigo-300/40" />
        </FloatingShape>
        <FloatingShape delay={2} duration={10} initialX={500} initialY={100}>
          <Square className="w-7 h-7 text-purple-300/40" />
        </FloatingShape>
        <FloatingShape delay={3} duration={7} initialX={700} initialY={250}>
          <Star className="w-5 h-5 text-pink-300/40" />
        </FloatingShape>
        <FloatingShape delay={4} duration={9} initialX={150} initialY={350}>
          <Heart className="w-6 h-6 text-rose-300/40" />
        </FloatingShape>

        {/* Orbiting icons */}
        <div className="absolute top-1/4 left-1/4">
          <OrbitingIcon icon={Shield} radius={80} duration={15} delay={0} size={20} />
          <OrbitingIcon icon={Zap} radius={120} duration={20} delay={3} size={18} />
          <OrbitingIcon icon={Sparkles} radius={100} duration={18} delay={6} size={16} />
        </div>

        <div className="absolute bottom-1/4 right-1/4">
          <OrbitingIcon icon={Phone} radius={90} duration={22} delay={2} size={19} />
          <OrbitingIcon icon={Star} radius={130} duration={16} delay={5} size={17} />
        </div>

        {/* Sparkles */}
        {sparkles.map((sparkle) => (
          <Sparkle key={sparkle.id} delay={sparkle.delay} x={sparkle.x} y={sparkle.y} />
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="w-full max-w-3xl"
        >
          <div className="shadow-2xl bg-white/80 backdrop-blur-lg rounded-3xl overflow-hidden border border-white/20 flex flex-col lg:flex-row">
            <div className="w-full lg:w-1/2 p-6 sm:p-8 flex flex-col justify-center relative">
              <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent pointer-events-none" />

              <motion.div variants={itemVariants} className="relative z-10 max-w-sm mx-auto">
                <motion.div
                  className="text-center mb-6"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <motion.img
                    src="/cladoclogo.webp"
                    alt="Citriot"
                    className="w-25 h-16 mx-auto mb-4"
                    transition={{ duration: 0.8 }}
                  />
                  <h1 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
                    Welcome!
                  </h1>
                  <p className="text-gray-600 text-xs sm:text-sm">Sign in to continue</p>
                </motion.div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <motion.div variants={itemVariants}>
                    <label className="block text-gray-700 font-medium mb-1">Username</label>
                    <div className="relative">
                      <User
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        size={18}
                      />
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        placeholder="Enter your username"
                        className={`w-full pl-10 pr-3 py-2 text-sm rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.username
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-200 hover:border-blue-400"
                        }`}
                        aria-label="Username"
                      />
                    </div>
                    {errors.username && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.username}
                      </motion.p>
                    )}
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <label className="block text-gray-700 font-medium mb-1">Password</label>
                    <div className="relative">
                      <Lock
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        size={18}
                      />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter your password"
                        className={`w-full pl-10 pr-10 py-2 text-sm rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.password
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-200 hover:border-blue-400"
                        }`}
                        aria-label="Password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {errors.password && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.password}
                      </motion.p>
                    )}
                  </motion.div>

                  <motion.div
                    variants={itemVariants}
                    className="flex flex-col sm:flex-row sm:justify-between items-center gap-3"
                  >
                    <label className="flex items-center space-x-2 text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        name="remember"
                        checked={formData.remember}
                        onChange={handleInputChange}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs">Remember me</span>
                    </label>
                    <motion.button
                      type="button"
                      onClick={() => setShowForgotPasswordModal(true)}
                      className="text-xs text-blue-500 hover:text-blue-600 transition-colors duration-200"
                      whileHover={{ scale: 1.05 }}
                    >
                      Forgot password?
                    </motion.button>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-full py-2.5 text-base font-semibold rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-none hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 ${
                        loading ? "opacity-75 cursor-not-allowed" : ""
                      }`}
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center">
                          <svg
                            className="animate-spin h-4 w-4 mr-1.5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Logging in...
                        </span>
                      ) : (
                        <>
                          <LogIn size={18} />
                          <span>Sign In</span>
                        </>
                      )}
                    </motion.button>
                  </motion.div>
                </form>
              </motion.div>
            </div>

            <div className="w-full lg:w-1/2 bg-gradient-to-tl from-blue-500 to-indigo-500 flex items-center justify-center">
              <motion.img
                src="/login.webp"
                alt="Login illustration"
                initial={{ y: -10 }}
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 4 }}
                className="w-2/3"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default LoginPage;